import { ConsumerUnit } from 'entities/ConsumerUnit';
import { Invoice } from 'entities/Invoice';

/**
 * Estimativa de referência de economia por UC — fonte única (out/2026).
 *
 * Por quê: Serviços Adicionais e os dois cards "Economize até" do Dashboard
 * tinham três fórmulas diferentes para a mesma promessa (RepLago Acampamento:
 * R$ 7.022,92/ano no Dashboard × R$ 310,04/mês = R$ 3.720/ano em Serviços) e a
 * tela de Serviços dizia "calculado com base no histórico" para um percentual
 * fixo. Agora as três telas usam esta função, e o texto diz que é estimativa.
 *
 * Regra: percentual fixo sobre a fatura média dos últimos 12 meses com valor —
 * 30% para UC sem solar (instalação de sistema próprio) e 10% para UC com solar
 * (otimização). NÃO é o motor: a economia calculada vem das oportunidades
 * (`/analysis`, admin) e da calculadora solar; trocar esta estimativa por elas
 * exige decidir o que mostrar ao cliente sem análise rodada.
 */
export const REFERENCE_SAVINGS_RATE = {
  withSolar: 0.1,
  withoutSolar: 0.3,
} as const;

const AVERAGE_WINDOW = 12;

export interface ReferenceSavings {
  /** Fatura média (R$) dos últimos 12 meses com valor. */
  avgMonthlyInvoice: number;
  /** Percentual aplicado (fração). */
  rate: number;
  /** Economia estimada em R$/mês. */
  monthlySavings: number;
  /** Economia estimada em R$/ano (12 × mensal). */
  annualSavings: number;
}

/**
 * Fatura média da UC: as 12 faturas mais recentes com valor > 0 (o placeholder
 * "Importar fatura" vem com valor negativo). Sem fatura, usa o valor da última
 * fatura gravado na UC.
 */
export const averageMonthlyInvoice = (
  invoices: Pick<Invoice, 'refMonth' | 'invoiceValue'>[],
  fallback = 0
): number => {
  const recent = invoices
    .filter((inv) => inv.invoiceValue > 0)
    .sort((a, b) => (a.refMonth < b.refMonth ? 1 : -1))
    .slice(0, AVERAGE_WINDOW);

  if (recent.length === 0) return fallback > 0 ? fallback : 0;

  return recent.reduce((sum, inv) => sum + inv.invoiceValue, 0) / recent.length;
};

export const referenceSavings = (
  consumerUnit: Pick<ConsumerUnit, 'hasFv' | 'lastInvoiceValue'>,
  invoices: Pick<Invoice, 'refMonth' | 'invoiceValue'>[]
): ReferenceSavings => {
  const avgMonthlyInvoice = averageMonthlyInvoice(
    invoices,
    consumerUnit.lastInvoiceValue
  );
  const rate = consumerUnit.hasFv
    ? REFERENCE_SAVINGS_RATE.withSolar
    : REFERENCE_SAVINGS_RATE.withoutSolar;
  const monthlySavings = avgMonthlyInvoice * rate;

  return {
    avgMonthlyInvoice,
    rate,
    monthlySavings,
    annualSavings: monthlySavings * 12,
  };
};

/** Texto curto da regra, para as telas não repetirem o percentual à mão. */
export const REFERENCE_SAVINGS_NOTE = `Estimativa de referência: ${Math.round(
  REFERENCE_SAVINGS_RATE.withoutSolar * 100
)}% da fatura média dos últimos 12 meses para imóveis sem solar e ${Math.round(
  REFERENCE_SAVINGS_RATE.withSolar * 100
)}% para imóveis com solar. O valor real depende de um estudo do seu caso.`;
